import {useRef, useCallback} from 'react'

const FOCUSABLE_SELECTOR = [
    'button:not([disabled])',
    '[href]',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
].join(', ')

const focusWithoutScroll = (element) => {
    if (!element?.focus) return

    try {
        element.focus({preventScroll: true})
    } catch (error) {
        element.focus()
    }
}

/**
 * Simple hook for smooth scrolling to an element
 * @param {number} offset - Offset from top in pixels (default: 80)
 * @returns {Object} { ref, scrollToElement }
 */
export const useScrollTo = (offset = 80) => {
    const elementRef = useRef(null)

    const scrollToElement = useCallback((event) => {
        if (!elementRef.current) {
            return
        }

        const elementPosition = elementRef.current.offsetTop
        const offsetPosition = Math.max(0, elementPosition - offset)
        const shouldMoveFocus = event?.type === 'keydown'

        window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
        })

        if (shouldMoveFocus) {
            window.requestAnimationFrame(() => {
                const firstFocusableElement = elementRef.current?.querySelector(FOCUSABLE_SELECTOR)

                if (firstFocusableElement) {
                    focusWithoutScroll(firstFocusableElement)
                    return
                }

                if (!elementRef.current?.hasAttribute('tabindex')) {
                    elementRef.current?.setAttribute('tabindex', '-1')
                }

                focusWithoutScroll(elementRef.current)
            })
        }
    }, [offset])

    return {ref: elementRef, scrollToElement}
}
